"use client";

import React, {
  FC,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@/app/lib/hooks";

export type Destination =
  | { type: "city"; slug: string; name: string; country?: string; countryCode?: string }
  | { type: "country"; slug: string; name: string; countryCode?: string };

interface FeaturedCity {
  id: number | string;
  name: string;
  slug: string;
  totalTours?: number;
  tourCount?: number;
  countryInfo?: { code?: string; name?: string };
}

interface FlatCity {
  cityName: string;
  cityDisplayName: string;
  citySlug: string;
  countryName: string;
  countryDisplayName: string;
  countrySlug: string;
  countryCode?: string;
}

export interface SearchModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * When provided, picking a destination calls back with the selection and
   * the modal closes — no navigation. When omitted, the modal navigates to
   * /city/[slug] or /country/[slug] directly (legacy topbar behavior).
   */
  onSelect?: (dest: Destination) => void;
}

/**
 * Full-screen destination picker.
 *
 * Data sources:
 *   - Empty query → featured cities from Redux (`state.cities.featured`),
 *     rendered under a "Destinations populaires" header.
 *   - Query ≥ 3 chars → results from `/api/citiesPerCountry` (same
 *     endpoint the legacy AutoComplete consumes), grouped by country.
 *     Country headers are themselves clickable (route to the country
 *     page / passed through onSelect as a country pick).
 */
const SearchModal: FC<SearchModalProps> = ({ open, onClose, onSelect }) => {
  const { t } = useTranslation("guydeez");
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState("");
  const [allCities, setAllCities] = useState<FlatCity[]>([]);

  const selectedLanguage = useAppSelector(
    (s) => s.language.selectedLanguage || "fr_FR"
  );
  const featured: FeaturedCity[] = useAppSelector((s) => s.cities.featured) || [];

  // Reset query whenever the modal re-opens.
  useEffect(() => {
    if (open) setQuery("");
  }, [open]);

  // Fetch /api/citiesPerCountry once per language. The endpoint is small
  // enough (a few KB) to keep in component state.
  useEffect(() => {
    const base = process.env.NEXT_PUBLIC_SOCKET || "";
    if (!base) return;
    let aborted = false;
    fetch(
      `${base}/api/citiesPerCountry?lang=${encodeURIComponent(selectedLanguage)}`,
      { headers: { Accept: "application/json" } }
    )
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (aborted || !Array.isArray(data)) return;
        const flat: FlatCity[] = data.flatMap((country: any) =>
          (country.city || []).map((city: any) => ({
            cityName: city.name,
            cityDisplayName: city.nameTranslated || city.name,
            citySlug: (city.slug || city.name || "")
              .toString()
              .replace(/\s+/g, "-")
              .toLowerCase(),
            countryName: country.name,
            countryDisplayName: country.nameTranslated || country.name,
            countrySlug: country.slug,
            countryCode: country.code,
          }))
        );
        setAllCities(flat);
      })
      .catch(() => {});
    return () => {
      aborted = true;
    };
  }, [selectedLanguage]);

  const normalize = (s: string) =>
    s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

  const q = query.trim();
  const isSearching = q.length >= 3;

  const searchResults = useMemo(() => {
    if (!isSearching) return null;
    const nq = normalize(q);
    return allCities.filter(
      (c) =>
        normalize(c.cityDisplayName).includes(nq) ||
        normalize(c.countryDisplayName).includes(nq) ||
        normalize(c.countryName).includes(nq)
    );
  }, [isSearching, q, allCities]);

  // Group search results by country.
  const groupedResults = useMemo(() => {
    if (!searchResults) return null;
    const map = new Map<string, { country: FlatCity; cities: FlatCity[] }>();
    searchResults.forEach((c) => {
      const key = c.countrySlug || c.countryName;
      if (!map.has(key)) map.set(key, { country: c, cities: [] });
      map.get(key)!.cities.push(c);
    });
    return Array.from(map.values()).slice(0, 8);
  }, [searchResults]);

  const handlePick = (dest: Destination) => {
    if (onSelect) {
      onSelect(dest);
      onClose();
      return;
    }
    // Legacy: navigate directly
    const path =
      dest.type === "city" ? `/city/${dest.slug}` : `/country/${dest.slug}`;
    router.push(path as any);
    onClose();
  };

  const pickFeaturedCity = (c: FeaturedCity) =>
    handlePick({
      type: "city",
      slug: c.slug,
      name: c.name,
      country: c.countryInfo?.name,
      countryCode: c.countryInfo?.code,
    });

  return (
    <Transition show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[200]"
        onClose={onClose}
        initialFocus={inputRef}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 pt-[10vh]">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-95"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-2 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl overflow-hidden">
                {/* Search input row — editorial-style: burgundy icon &
                    bold placeholder, no border ring, cream ESC chip. */}
                <div className="flex items-center gap-4 px-6 py-5 border-b border-neutral-100">
                  <svg
                    width={20}
                    height={20}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#5B1F3A"
                    strokeWidth="2.25"
                    strokeLinecap="round"
                    className="flex-shrink-0"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <path d="M20.5 20.5l-4.6-4.6" />
                  </svg>
                  <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t("search_modal_placeholder", {
                      defaultValue: "Où partez-vous ? Ville, pays…",
                    })}
                    className="flex-1 bg-transparent outline-none ring-0 focus:outline-none focus:ring-0 border-0 text-[17px] text-[#5B1F3A] placeholder:text-[#5B1F3A] placeholder:font-bold placeholder:opacity-90 font-bold tracking-tight"
                  />
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="text-[10px] font-bold tracking-widest text-[#5B1F3A]/70 px-2.5 py-1 rounded-md bg-[#F8E0E5] hover:bg-[#f3cfd6]"
                  >
                    ESC
                  </button>
                </div>

                {/* Results */}
                <div className="max-h-[60vh] overflow-y-auto py-3">
                  {!isSearching ? (
                    <>
                      <SectionHeader
                        text={t("search_modal_section_popular", {
                          defaultValue: "Destinations populaires",
                        })}
                      />
                      {featured.length === 0 ? (
                        <Empty
                          text={t("search_modal_loading_featured", {
                            defaultValue: "Chargement…",
                          })}
                        />
                      ) : (
                        <ul className="flex flex-col">
                          {featured.map((c) => (
                            <li key={c.id}>
                              <Row
                                onClick={() => pickFeaturedCity(c)}
                                badge={c.countryInfo?.code}
                                title={capitalize(c.name)}
                                subtitle={[
                                  capitalize(c.countryInfo?.name),
                                  (c.totalTours || c.tourCount)
                                    ? `${c.totalTours || c.tourCount} tours`
                                    : null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <>
                      {!groupedResults || groupedResults.length === 0 ? (
                        <Empty
                          text={t("search_modal_empty", {
                            defaultValue: "Aucune destination ne correspond.",
                          })}
                        />
                      ) : (
                        groupedResults.map(({ country, cities }) => (
                          <div key={country.countrySlug} className="mb-2">
                            <button
                              type="button"
                              onClick={() =>
                                handlePick({
                                  type: "country",
                                  slug: country.countrySlug,
                                  name: country.countryDisplayName,
                                  countryCode: country.countryCode,
                                })
                              }
                              className="w-full text-left px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#C13B5C] hover:bg-neutral-50"
                            >
                              {capitalize(country.countryDisplayName)}
                            </button>
                            <ul className="flex flex-col">
                              {cities.map((c) => (
                                <li key={c.citySlug + c.cityName}>
                                  <Row
                                    onClick={() =>
                                      handlePick({
                                        type: "city",
                                        slug: c.citySlug,
                                        name: capitalize(c.cityDisplayName),
                                        country: capitalize(c.countryDisplayName),
                                        countryCode: c.countryCode,
                                      })
                                    }
                                    badge={c.countryCode}
                                    title={capitalize(c.cityDisplayName)}
                                    subtitle={capitalize(c.countryDisplayName)}
                                  />
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))
                      )}
                    </>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

const SectionHeader: FC<{ text: string }> = ({ text }) => (
  <div className="px-5 py-2 text-[10px] font-bold uppercase tracking-widest text-neutral-500">
    {text}
  </div>
);

const Empty: FC<{ text: string }> = ({ text }) => (
  <div className="px-5 py-6 text-sm text-neutral-500">{text}</div>
);

const Row: FC<{
  onClick: () => void;
  badge?: string;
  title: string;
  subtitle?: string;
}> = ({ onClick, badge, title, subtitle }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full flex items-center gap-4 px-5 py-3 hover:bg-neutral-50 text-left"
  >
    <FlagBadge code={badge} alt={title} />
    <div className="flex-1 min-w-0">
      <div className="text-base font-bold text-[#5B1F3A] truncate">{title}</div>
      {subtitle && (
        <div className="text-xs text-neutral-500 truncate">{subtitle}</div>
      )}
    </div>
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="text-neutral-300 flex-shrink-0"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  </button>
);

/**
 * Country flag rendered in a circular frame, sized to match the previous
 * 40 px avatar slot. Source: flagcdn.com (SVG, free, no API key, CC0).
 * Falls back to a `??` placeholder if the code is missing/invalid.
 */
const FlagBadge: FC<{ code?: string; alt: string }> = ({ code, alt }) => {
  const c = (code || "").toLowerCase();
  if (!c || c.length !== 2) {
    return (
      <div
        className="w-10 h-10 rounded-full bg-[#F8E0E5] text-[#5B1F3A] flex items-center justify-center text-xs font-bold flex-shrink-0"
        aria-hidden
      >
        ??
      </div>
    );
  }
  return (
    <div
      className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-neutral-100"
      style={{ boxShadow: "0 0 0 1px rgba(0,0,0,0.08)" }}
    >
      <img
        src={`https://flagcdn.com/${c}.svg`}
        alt={alt}
        width={40}
        height={40}
        loading="lazy"
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
    </div>
  );
};

const capitalize = (s?: string) => {
  if (!s) return "";
  return s
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
};

export default SearchModal;
